import * as amplitude from "@amplitude/analytics-browser";
import { ApolloError } from "@apollo/client";
import { Box, Button, Flex } from "@chakra-ui/react";
import React from "react";
import { RiEditLine } from "react-icons/ri";
import { Link, useParams } from "react-router-dom";
import { ApolloError as ApolloErrorView } from "src/components/Feedback/ApolloError";
import { GenericError } from "src/components/Feedback/GenericError";
import { NotFound } from "src/components/Feedback/NotFound";
import { Section } from "src/components/Form/Section";
import { ParentFormsLayoutFooter } from "src/components/Layout/ParentFormsLayoutFooter";
import { ParentFormsLayoutInner } from "src/components/Layout/ParentFormsLayoutInner";
import { ParentFormsLayoutOuter } from "src/components/Layout/ParentFormsLayoutOuter";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import {
  FormProvider,
  useFormContext,
  useFormTemplateContext,
  useVisibilityContext,
} from "src/components/Providers/FormProvider";
import { Glossary } from "src/components/Text/Glossary";
import { useOrganization } from "src/hooks/useOrganization";
import { usePreviousFormSchools } from "src/hooks/usePreviousFormSchools";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { GET_FORM_VIEW_BY_ID } from "src/scenes/orgAdmin/forms/graphql/queries";
import {
  canEditFormCheck,
  canLateEditCheck,
} from "src/scenes/parent/forms/formEditability";
import { isSectionApplicableForView } from "src/services/formTemplate/section";
import * as Url from "src/services/url";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { StudentHeader } from "./components/Layout/StudentHeader";
import { Loading } from "./components/Loading";
import { GET_SCHOOLS_RANK } from "./graphql/queries";
import { useSendFormBackToInProgress } from "./hooks/useSendFormToInProgress";

function handleError(error: Error | ApolloError): React.ReactElement {
  if (error instanceof ApolloError) {
    return <ApolloErrorView error={error} />;
  }

  return <GenericError />;
}

export const ViewForm: React.FC = () => {
  const { id } = useParams();
  const organization = useOrganization();

  const { remoteData: formViewRD } = useRemoteDataQuery<
    GQL.GetFormViewById,
    GQL.GetFormViewByIdVariables
  >(GET_FORM_VIEW_BY_ID, {
    variables: {
      form_id: id ?? "",
      organization_id: organization.toNullable()?.id ?? "",
      is_admin: false,
    },
    skip: !id || !organization.hasData(),
  });

  const { remoteData: getSchoolsRankRD } = useRemoteDataQuery<
    GQL.GetSchoolsRank,
    GQL.GetSchoolsRankVariables
  >(GET_SCHOOLS_RANK, {
    variables: {
      form_id: id ?? "",
    },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  const previousFormSchoolIdsRD = usePreviousFormSchools(id);

  if (!id) return <NotFound />;

  return (
    <FormProvider formId={id}>
      {(formTemplateContextRD, formContextRD) => (
        <ParentFormsLayoutOuter
          backLink={organization
            .map((org) => ({
              url: Url.Parent.index(org),
              label: "Back to forms",
            }))
            .withDefault(undefined)}
          heading="View form"
        >
          <RemoteDataView
            loading={<Loading />}
            error={handleError}
            remoteData={RD.toTuple5(
              getSchoolsRankRD,
              previousFormSchoolIdsRD,
              formViewRD,
              formTemplateContextRD,
              formContextRD
            )}
          >
            {([getSchoolsRank, previousFormSchoolIds, formView]) => {
              return (
                <ViewFormContent
                  formId={id}
                  getSchoolsRank={getSchoolsRank}
                  previousFormSchoolIds={previousFormSchoolIds}
                  formView={formView}
                />
              );
            }}
          </RemoteDataView>
        </ParentFormsLayoutOuter>
      )}
    </FormProvider>
  );
};

type ViewFormContentProps = {
  formId: uuid;
  formView: GQL.GetFormViewById;
  getSchoolsRank: GQL.GetSchoolsRank;
  previousFormSchoolIds: uuid[];
};
const ViewFormContent: React.FC<ViewFormContentProps> = ({
  formId,
  formView,
  getSchoolsRank,
  previousFormSchoolIds,
}) => {
  const organization = useOrganization();
  const formTemplateContext = useFormTemplateContext();
  const formContext = useFormContext();

  const rankedSchoolIds = getSchoolsRank.form_school_rank.map(
    (rankedSchool) => rankedSchool.school.id
  );

  const visibilityContext = useVisibilityContext();

  const { isSendingFormToInProgress, sendFormBackToInProgress } =
    useSendFormBackToInProgress({ formId });

  const form = formView.form_by_pk;
  if (!form) {
    return <NotFound />;
  }

  const formTemplateGql = formTemplateContext.gql;
  if (!formTemplateGql) {
    return <NotFound />;
  }

  const sections = formTemplateContext.formTemplate.sections.filter(
    (section) => {
      return isSectionApplicableForView(
        formContext.status,
        section,
        formContext.gql.form_answer,
        formContext.gql.grades_answer,
        formContext.gql.form_address,
        formContext.gql.custom_question_answer,
        { rankedSchoolIds, previousFormSchoolIds },
        visibilityContext
      );
    }
  ) as AF.Sections<AF.WithId>;

  const canEditForm = canEditFormCheck(
    formTemplateGql.sections ?? [],
    formTemplateGql.enrollment_period,
    formContext.status
  );

  const canLateEdit = canLateEditCheck(
    formTemplateGql.sections ?? [],
    formTemplateGql.enrollment_period,
    formContext.status
  );

  let footers;
  if (canEditForm) {
    footers = (
      <ParentFormsLayoutFooter justifyContent="stretch">
        <Button
          isLoading={isSendingFormToInProgress}
          flexGrow={1}
          leftIcon={<RiEditLine />}
          onClick={() => {
            amplitude.track("Edit form clicked", {
              button_location: "bottom_bar",
            });
            sendFormBackToInProgress();
          }}
        >
          Edit form
        </Button>
      </ParentFormsLayoutFooter>
    );
  } else if (canLateEdit) {
    footers = (
      <ParentFormsLayoutFooter justifyContent="stretch">
        <Button
          as={Link}
          flexGrow={1}
          leftIcon={<RiEditLine />}
          to={organization
            .map((org) => Url.Parent.Form.lateEdit(org, formId))
            .withDefault("#")}
        >
          <Glossary>Edit</Glossary>
        </Button>
      </ParentFormsLayoutFooter>
    );
  }

  return (
    <ParentFormsLayoutInner footers={footers}>
      <Flex direction="column" gap={4}>
        <Box mx={4} mt={4}>
          <StudentHeader
            student={form.person}
            enrollmentPeriodName={formTemplateGql?.enrollment_period.name ?? ""}
          />
        </Box>

        <Flex direction="column" paddingX={6}>
          {sections.map((section) =>
            section ? (
              <Section
                key={section.id}
                form={form}
                allSections={sections}
                section={section}
                form_answers={formContext.gql.form_answer}
                grades_answers={formContext.gql.grades_answer}
                address_answers={formContext.gql.form_address}
                custom_question_answers={formContext.gql.custom_question_answer}
                schoolsRank={getSchoolsRank.form_school_rank}
                onEdit={() => {}}
                onCloseEdit={() => {}}
                editMode={"disabled"}
                isLateFormEdit={false}
                previousFormSchoolIds={previousFormSchoolIds}
                formDisclaimer={form.form_disclaimers[0]}
              />
            ) : null
          )}
        </Flex>
      </Flex>
    </ParentFormsLayoutInner>
  );
};
